import { Course } from '../courses/types';

export const UNREAL_GAME_DEVELOPMENT: Course = {
  id: 'unreal-game-development',
  title: 'Unreal Engine Game Development',
  description: 'Create AAA-quality games with Unreal Engine using Blueprints and C++.',
  level: 'advanced',
  duration: '10-12 weeks',
  estimatedHours: 50,
  tags: ['Unreal Engine', 'C++', 'Blueprints', 'Game Development', 'Graphics'],
  prerequisites: ['Basic C++ programming', 'Object-oriented programming', '3D mathematics'],
  learningOutcomes: [
    'Master Unreal Engine editor and tools',
    'Create games using Blueprint visual scripting',
    'Implement gameplay mechanics with C++',
    'Build high-quality 3D environments',
    'Optimize games for multiple platforms',
    'Deploy commercial-quality games'
  ],
  modules: [
    {
      id: 'unreal-fundamentals',
      title: 'Unreal Engine Fundamentals',
      description: 'Learn the Unreal Engine interface, core concepts, and Blueprint basics.',
      duration: '3 hours',
      difficulty: 'beginner',
      content: `
# Unreal Engine Fundamentals

## Engine Overview
Unreal Engine is a complete suite of development tools for creating high-quality games and real-time 3D content.

## Editor Interface

### Main Components
- **Viewport**: 3D scene visualization and manipulation
- **World Outliner**: Hierarchical view of all actors in the level
- **Content Browser**: Asset management system
- **Details Panel**: Properties of selected objects
- **Blueprint Editor**: Visual scripting environment

### Core Concepts
- **Actors**: Objects that can be placed in the world
- **Components**: Building blocks that define actor behavior
- **Blueprints**: Visual scripting system
- **Levels**: Game world containers
- **Game Mode**: Rules and logic for gameplay

## Blueprint Basics

### Creating Your First Blueprint
\`\`\`cpp
// Example Actor Blueprint structure
UCLASS(BlueprintType, Blueprintable)
class MYGAME_API AMyActor : public AActor
{
    GENERATED_BODY()

public:
    AMyActor();

protected:
    virtual void BeginPlay() override;

    UPROPERTY(VisibleAnywhere, BlueprintReadOnly, Category = Components)
    class UStaticMeshComponent* MeshComponent;

    UPROPERTY(EditAnywhere, BlueprintReadWrite, Category = "My Settings")
    float Speed = 100.0f;

public:
    virtual void Tick(float DeltaTime) override;

    UFUNCTION(BlueprintCallable, Category = "My Functions")
    void DoSomething();
};
\`\`\`

### Blueprint Event Graph
- **Event BeginPlay**: Triggered when the game starts
- **Event Tick**: Called every frame
- **Custom Events**: User-defined events
- **Functions**: Reusable logic blocks

## Asset Pipeline
- **Importing**: FBX, OBJ, textures, audio files
- **Materials**: Advanced shader creation
- **Skeletal Meshes**: Character and animation assets
- **Static Meshes**: Environment and prop geometry
      `,
      learningObjectives: [
        'Navigate Unreal Engine editor',
        'Understand Actors and Components',
        'Create basic Blueprint scripts',
        'Import and manage game assets'
      ]
    },
    {
      id: 'unreal-blueprints',
      title: 'Blueprint Visual Scripting',
      description: 'Master Blueprint visual scripting for rapid game development.',
      duration: '4 hours',
      difficulty: 'intermediate',
      content: `
# Blueprint Visual Scripting

## Blueprint Types

### Level Blueprint
- Controls level-specific logic
- Manages global events and triggers
- Coordinates between different actors

### Actor Blueprint
- Defines individual object behavior
- Contains components and custom logic
- Can be spawned dynamically

### Widget Blueprint
- Creates user interface elements
- Handles UI events and interactions
- Manages HUD and menu systems

## Variables and Data Types

### Common Blueprint Variables
\`\`\`cpp
// C++ equivalent of Blueprint variables
UPROPERTY(EditAnywhere, BlueprintReadWrite, Category = "Player Stats")
float Health = 100.0f;

UPROPERTY(EditAnywhere, BlueprintReadWrite, Category = "Player Stats")
int32 Score = 0;

UPROPERTY(EditAnywhere, BlueprintReadWrite, Category = "Player Stats")
bool bIsAlive = true;

UPROPERTY(EditAnywhere, BlueprintReadWrite, Category = "References")
AActor* TargetActor;

UPROPERTY(EditAnywhere, BlueprintReadWrite, Category = "Collections")
TArray<FString> InventoryItems;
\`\`\`

## Functions and Macros

### Custom Functions
\`\`\`cpp
UFUNCTION(BlueprintCallable, Category = "Health System")
void TakeDamage(float DamageAmount)
{
    Health -= DamageAmount;
    
    if (Health <= 0)
    {
        Health = 0;
        bIsAlive = false;
        OnPlayerDeath();
    }
}

UFUNCTION(BlueprintCallable, Category = "Health System")
void Heal(float HealAmount)
{
    Health = FMath::Clamp(Health + HealAmount, 0.0f, 100.0f);
}

UFUNCTION(BlueprintImplementableEvent, Category = "Health System")
void OnPlayerDeath();
\`\`\`

## Blueprint Communication

### Direct Reference
- Store reference to target Blueprint
- Call functions directly
- Access public variables

### Casting
- Convert generic actor reference to specific type
- Access specialized functions and variables

### Interface Implementation
\`\`\`cpp
// Blueprint Interface example
UINTERFACE(MinimalAPI, Blueprintable)
class UInteractable : public UInterface
{
    GENERATED_BODY()
};

class IInteractable
{
    GENERATED_BODY()

public:
    UFUNCTION(BlueprintNativeEvent, BlueprintCallable, Category = "Interaction")
    void Interact(AActor* InteractingActor);
    virtual void Interact_Implementation(AActor* InteractingActor) = 0;
};
\`\`\`

## Event System

### Event Dispatchers
\`\`\`cpp
DECLARE_DYNAMIC_MULTICAST_DELEGATE_OneParam(FOnHealthChanged, float, NewHealth);

UPROPERTY(BlueprintAssignable, Category = "Events")
FOnHealthChanged OnHealthChanged;

// Call the event
void UpdateHealth(float NewHealth)
{
    Health = NewHealth;
    OnHealthChanged.Broadcast(Health);
}
\`\`\`

### Collision Events
\`\`\`cpp
UFUNCTION()
void OnOverlapBegin(UPrimitiveComponent* OverlappedComp, AActor* OtherActor, 
                   UPrimitiveComponent* OtherComp, int32 OtherBodyIndex, 
                   bool bFromSweep, const FHitResult& SweepResult);

UFUNCTION()
void OnOverlapEnd(UPrimitiveComponent* OverlappedComp, AActor* OtherActor, 
                 UPrimitiveComponent* OtherComp, int32 OtherBodyIndex);
\`\`\`

## Advanced Blueprint Techniques

### Timeline Component
- Smooth animation and interpolation
- Custom curve-based animations
- Time-based events and triggers

### AI Behavior Trees
- Decision-making logic for NPCs
- Conditional behavior execution
- Reusable AI components

### Save/Load System
\`\`\`cpp
USTRUCT(BlueprintType)
struct FPlayerSaveData
{
    GENERATED_BODY()

    UPROPERTY(EditAnywhere, BlueprintReadWrite)
    float Health;

    UPROPERTY(EditAnywhere, BlueprintReadWrite)
    int32 Score;

    UPROPERTY(EditAnywhere, BlueprintReadWrite)
    FVector PlayerLocation;

    UPROPERTY(EditAnywhere, BlueprintReadWrite)
    TArray<FString> Inventory;
};
\`\`\`
      `,
      learningObjectives: [
        'Create complex Blueprint logic',
        'Implement Blueprint communication',
        'Use event systems effectively',
        'Build modular Blueprint components'
      ]
    },
    {
      id: 'unreal-cpp',
      title: 'C++ Programming in Unreal',
      description: 'Implement high-performance game systems using Unreal C++.',
      duration: '5 hours',
      difficulty: 'advanced',
      content: `
# C++ Programming in Unreal Engine

## Unreal C++ Fundamentals

### Project Setup
\`\`\`cpp
// MyGame.h - Main game module header
#pragma once

#include "CoreMinimal.h"

class FMyGameModule : public IModuleInterface
{
public:
    virtual void StartupModule() override;
    virtual void ShutdownModule() override;
};
\`\`\`

### Basic Actor Class
\`\`\`cpp
// MyActor.h
#pragma once

#include "CoreMinimal.h"
#include "GameFramework/Actor.h"
#include "MyActor.generated.h"

UCLASS()
class MYGAME_API AMyActor : public AActor
{
    GENERATED_BODY()
    
public:    
    AMyActor();

protected:
    virtual void BeginPlay() override;

    UPROPERTY(VisibleAnywhere, BlueprintReadOnly, Category = Components)
    class UStaticMeshComponent* MeshComponent;

    UPROPERTY(VisibleAnywhere, BlueprintReadOnly, Category = Components)
    class USphereComponent* CollisionComponent;

    UPROPERTY(EditAnywhere, BlueprintReadWrite, Category = "Movement")
    float MovementSpeed = 100.0f;

    UPROPERTY(EditAnywhere, BlueprintReadWrite, Category = "Movement")
    FVector MovementDirection = FVector::ZeroVector;

public:    
    virtual void Tick(float DeltaTime) override;

    UFUNCTION(BlueprintCallable, Category = "Movement")
    void SetMovementDirection(FVector NewDirection);

private:
    UFUNCTION()
    void OnCollisionBeginOverlap(UPrimitiveComponent* OverlappedComponent, 
                                AActor* OtherActor, UPrimitiveComponent* OtherComp, 
                                int32 OtherBodyIndex, bool bFromSweep, 
                                const FHitResult& SweepResult);
};

// MyActor.cpp
#include "MyActor.h"
#include "Components/StaticMeshComponent.h"
#include "Components/SphereComponent.h"
#include "Engine/Engine.h"

AMyActor::AMyActor()
{
    PrimaryActorTick.bCanEverTick = true;

    // Create collision component
    CollisionComponent = CreateDefaultSubobject<USphereComponent>(TEXT("CollisionComponent"));
    RootComponent = CollisionComponent;
    CollisionComponent->SetSphereRadius(50.0f);

    // Create mesh component
    MeshComponent = CreateDefaultSubobject<UStaticMeshComponent>(TEXT("MeshComponent"));
    MeshComponent->SetupAttachment(RootComponent);

    // Bind collision events
    CollisionComponent->OnComponentBeginOverlap.AddDynamic(this, &AMyActor::OnCollisionBeginOverlap);
}

void AMyActor::BeginPlay()
{
    Super::BeginPlay();
    
    UE_LOG(LogTemp, Warning, TEXT("MyActor has spawned!"));
}

void AMyActor::Tick(float DeltaTime)
{
    Super::Tick(DeltaTime);
    
    // Move the actor
    FVector NewLocation = GetActorLocation() + (MovementDirection * MovementSpeed * DeltaTime);
    SetActorLocation(NewLocation);
}

void AMyActor::SetMovementDirection(FVector NewDirection)
{
    MovementDirection = NewDirection.GetSafeNormal();
}

void AMyActor::OnCollisionBeginOverlap(UPrimitiveComponent* OverlappedComponent, 
                                      AActor* OtherActor, UPrimitiveComponent* OtherComp, 
                                      int32 OtherBodyIndex, bool bFromSweep, 
                                      const FHitResult& SweepResult)
{
    if (OtherActor && OtherActor != this)
    {
        UE_LOG(LogTemp, Warning, TEXT("Overlapped with: %s"), *OtherActor->GetName());
    }
}
\`\`\`

## Player Controller and Input

### Enhanced Input System
\`\`\`cpp
// MyPlayerController.h
#pragma once

#include "CoreMinimal.h"
#include "GameFramework/PlayerController.h"
#include "InputActionValue.h"
#include "MyPlayerController.generated.h"

class UInputMappingContext;
class UInputAction;

UCLASS()
class MYGAME_API AMyPlayerController : public APlayerController
{
    GENERATED_BODY()

public:
    AMyPlayerController();

protected:
    virtual void BeginPlay() override;
    virtual void SetupInputComponent() override;

    UPROPERTY(EditAnywhere, BlueprintReadOnly, Category = Input)
    UInputMappingContext* DefaultMappingContext;

    UPROPERTY(EditAnywhere, BlueprintReadOnly, Category = Input)
    UInputAction* MoveAction;

    UPROPERTY(EditAnywhere, BlueprintReadOnly, Category = Input)
    UInputAction* LookAction;

    UPROPERTY(EditAnywhere, BlueprintReadOnly, Category = Input)
    UInputAction* JumpAction;

private:
    void Move(const FInputActionValue& Value);
    void Look(const FInputActionValue& Value);
    void Jump();
};

// MyPlayerController.cpp
#include "MyPlayerController.h"
#include "EnhancedInputSubsystems.h"
#include "EnhancedInputComponent.h"
#include "GameFramework/Character.h"

AMyPlayerController::AMyPlayerController()
{
    // Load input assets
    // These would be created in the editor
}

void AMyPlayerController::BeginPlay()
{
    Super::BeginPlay();

    if (UEnhancedInputLocalPlayerSubsystem* Subsystem = 
        ULocalPlayer::GetSubsystem<UEnhancedInputLocalPlayerSubsystem>(GetLocalPlayer()))
    {
        Subsystem->AddMappingContext(DefaultMappingContext, 0);
    }
}

void AMyPlayerController::SetupInputComponent()
{
    Super::SetupInputComponent();

    if (UEnhancedInputComponent* EnhancedInputComponent = 
        CastChecked<UEnhancedInputComponent>(InputComponent))
    {
        EnhancedInputComponent->BindAction(MoveAction, ETriggerEvent::Triggered, this, &AMyPlayerController::Move);
        EnhancedInputComponent->BindAction(LookAction, ETriggerEvent::Triggered, this, &AMyPlayerController::Look);
        EnhancedInputComponent->BindAction(JumpAction, ETriggerEvent::Started, this, &AMyPlayerController::Jump);
    }
}

void AMyPlayerController::Move(const FInputActionValue& Value)
{
    FVector2D MovementVector = Value.Get<FVector2D>();

    if (APawn* ControlledPawn = GetPawn())
    {
        // Add movement input
        ControlledPawn->AddMovementInput(ControlledPawn->GetActorForwardVector(), MovementVector.Y);
        ControlledPawn->AddMovementInput(ControlledPawn->GetActorRightVector(), MovementVector.X);
    }
}

void AMyPlayerController::Look(const FInputActionValue& Value)
{
    FVector2D LookAxisVector = Value.Get<FVector2D>();

    if (APawn* ControlledPawn = GetPawn())
    {
        ControlledPawn->AddControllerYawInput(LookAxisVector.X);
        ControlledPawn->AddControllerPitchInput(LookAxisVector.Y);
    }
}

void AMyPlayerController::Jump()
{
    if (ACharacter* Character = Cast<ACharacter>(GetPawn()))
    {
        Character->Jump();
    }
}
\`\`\`

## Game Framework Classes

### Game Mode
\`\`\`cpp
// MyGameMode.h
#pragma once

#include "CoreMinimal.h"
#include "GameFramework/GameModeBase.h"
#include "MyGameMode.generated.h"

UCLASS()
class MYGAME_API AMyGameMode : public AGameModeBase
{
    GENERATED_BODY()

public:
    AMyGameMode();

protected:
    virtual void BeginPlay() override;
    virtual void PostLogin(APlayerController* NewPlayer) override;
    virtual void Logout(AController* Exiting) override;

    UPROPERTY(EditAnywhere, BlueprintReadWrite, Category = "Game Rules")
    int32 MaxPlayers = 4;

    UPROPERTY(EditAnywhere, BlueprintReadWrite, Category = "Game Rules")
    float MatchDuration = 300.0f; // 5 minutes

    UPROPERTY(BlueprintReadOnly, Category = "Game State")
    int32 CurrentPlayerCount = 0;

    UPROPERTY(BlueprintReadOnly, Category = "Game State")
    float TimeRemaining;

public:
    UFUNCTION(BlueprintCallable, Category = "Game Management")
    void StartMatch();

    UFUNCTION(BlueprintCallable, Category = "Game Management")
    void EndMatch();

    UFUNCTION(BlueprintImplementableEvent, Category = "Game Events")
    void OnMatchStarted();

    UFUNCTION(BlueprintImplementableEvent, Category = "Game Events")
    void OnMatchEnded();

private:
    FTimerHandle MatchTimerHandle;
    bool bMatchInProgress = false;

    void UpdateMatchTimer();
};
\`\`\`

### Character Class
\`\`\`cpp
// MyCharacter.h
#pragma once

#include "CoreMinimal.h"
#include "GameFramework/Character.h"
#include "MyCharacter.generated.h"

UCLASS()
class MYGAME_API AMyCharacter : public ACharacter
{
    GENERATED_BODY()

public:
    AMyCharacter();

protected:
    virtual void BeginPlay() override;

    UPROPERTY(VisibleAnywhere, BlueprintReadOnly, Category = Camera)
    class USpringArmComponent* SpringArmComponent;

    UPROPERTY(VisibleAnywhere, BlueprintReadOnly, Category = Camera)
    class UCameraComponent* CameraComponent;

    UPROPERTY(EditAnywhere, BlueprintReadWrite, Category = "Character Stats")
    float MaxHealth = 100.0f;

    UPROPERTY(BlueprintReadOnly, Category = "Character Stats")
    float CurrentHealth;

    UPROPERTY(EditAnywhere, BlueprintReadWrite, Category = "Character Stats")
    float WalkSpeed = 300.0f;

    UPROPERTY(EditAnywhere, BlueprintReadWrite, Category = "Character Stats")
    float RunSpeed = 600.0f;

public:
    virtual void Tick(float DeltaTime) override;

    UFUNCTION(BlueprintCallable, Category = "Health")
    void TakeDamage(float DamageAmount);

    UFUNCTION(BlueprintCallable, Category = "Health")
    void Heal(float HealAmount);

    UFUNCTION(BlueprintCallable, Category = "Movement")
    void StartRunning();

    UFUNCTION(BlueprintCallable, Category = "Movement")
    void StopRunning();

    UFUNCTION(BlueprintImplementableEvent, Category = "Events")
    void OnHealthChanged(float NewHealth, float MaxHealth);

    UFUNCTION(BlueprintImplementableEvent, Category = "Events")
    void OnCharacterDeath();

private:
    bool bIsRunning = false;

    void UpdateMovementSpeed();
};
\`\`\`

## Memory Management and Optimization

### Smart Pointers and References
\`\`\`cpp
// Use TWeakPtr for optional references that might become invalid
UPROPERTY()
TWeakObjectPtr<AActor> OptionalActorReference;

// Use TSharedPtr for shared ownership (rare in gameplay code)
TSharedPtr<FMyCustomData> SharedData;

// Use raw pointers for guaranteed valid references (most common)
UPROPERTY()
AActor* GuaranteedActorReference;

// Safe access patterns
void SafeAccessExample()
{
    if (IsValid(GuaranteedActorReference))
    {
        GuaranteedActorReference->DoSomething();
    }

    if (OptionalActorReference.IsValid())
    {
        OptionalActorReference->DoSomething();
    }
}
\`\`\`

### Object Pooling in C++
\`\`\`cpp
// ObjectPool.h
template<typename T>
class TObjectPool
{
private:
    TArray<T*> AvailableObjects;
    TArray<T*> UsedObjects;
    UWorld* World;

public:
    TObjectPool(UWorld* InWorld, int32 PoolSize, UClass* ObjectClass)
        : World(InWorld)
    {
        for (int32 i = 0; i < PoolSize; ++i)
        {
            T* NewObject = World->SpawnActor<T>(ObjectClass);
            NewObject->SetActorHiddenInGame(true);
            NewObject->SetActorEnableCollision(false);
            AvailableObjects.Add(NewObject);
        }
    }

    T* GetObject()
    {
        if (AvailableObjects.Num() > 0)
        {
            T* Object = AvailableObjects.Pop();
            UsedObjects.Add(Object);
            Object->SetActorHiddenInGame(false);
            Object->SetActorEnableCollision(true);
            return Object;
        }
        return nullptr;
    }

    void ReturnObject(T* Object)
    {
        if (UsedObjects.Remove(Object) > 0)
        {
            Object->SetActorHiddenInGame(true);
            Object->SetActorEnableCollision(false);
            AvailableObjects.Add(Object);
        }
    }
};
\`\`\`
      `,
      learningObjectives: [
        'Write efficient Unreal C++ code',
        'Implement player controllers and input',
        'Create game framework classes',
        'Optimize memory usage and performance'
      ]
    },
    {
      id: 'unreal-environments',
      title: 'Level Design and Environments',
      description: 'Create stunning game environments using Unreal Engine tools.',
      duration: '4 hours',
      difficulty: 'intermediate',
      content: `
# Level Design and Environments

## Landscape and Terrain

### Landscape Creation
\`\`\`cpp
// Procedural landscape generation
UCLASS()
class MYGAME_API ALandscapeGenerator : public AActor
{
    GENERATED_BODY()

public:
    ALandscapeGenerator();

protected:
    UPROPERTY(EditAnywhere, BlueprintReadWrite, Category = "Landscape")
    int32 LandscapeSize = 1009; // Must be power of 2 + 1

    UPROPERTY(EditAnywhere, BlueprintReadWrite, Category = "Landscape")
    float HeightScale = 100.0f;

    UPROPERTY(EditAnywhere, BlueprintReadWrite, Category = "Landscape")
    float NoiseScale = 0.01f;

    UPROPERTY(EditAnywhere, BlueprintReadWrite, Category = "Landscape")
    UMaterialInterface* LandscapeMaterial;

public:
    UFUNCTION(CallInEditor = true, Category = "Generation")
    void GenerateLandscape();

private:
    void CreateHeightmap(TArray<uint16>& HeightData);
    float GenerateNoise(float X, float Y);
};

void ALandscapeGenerator::GenerateLandscape()
{
    TArray<uint16> HeightData;
    CreateHeightmap(HeightData);

    // Create landscape using heightmap data
    // This would typically be done through the Landscape API
    UE_LOG(LogTemp, Warning, TEXT("Landscape generated with %d height points"), HeightData.Num());
}

void ALandscapeGenerator::CreateHeightmap(TArray<uint16>& HeightData)
{
    HeightData.SetNum(LandscapeSize * LandscapeSize);

    for (int32 Y = 0; Y < LandscapeSize; Y++)
    {
        for (int32 X = 0; X < LandscapeSize; X++)
        {
            float Height = GenerateNoise(X * NoiseScale, Y * NoiseScale);
            Height = FMath::Clamp(Height, -1.0f, 1.0f);
            
            // Convert to 16-bit heightmap value
            uint16 HeightValue = (uint16)((Height + 1.0f) * 0.5f * 65535.0f);
            HeightData[Y * LandscapeSize + X] = HeightValue;
        }
    }
}

float ALandscapeGenerator::GenerateNoise(float X, float Y)
{
    // Simple Perlin noise implementation
    float Noise1 = FMath::PerlinNoise2D(FVector2D(X, Y));
    float Noise2 = FMath::PerlinNoise2D(FVector2D(X * 2, Y * 2)) * 0.5f;
    float Noise3 = FMath::PerlinNoise2D(FVector2D(X * 4, Y * 4)) * 0.25f;
    
    return Noise1 + Noise2 + Noise3;
}
\`\`\`

## Advanced Materials

### Dynamic Material System
\`\`\`cpp
// DynamicMaterialController.h
UCLASS()
class MYGAME_API ADynamicMaterialController : public AActor
{
    GENERATED_BODY()

public:
    ADynamicMaterialController();

protected:
    UPROPERTY(VisibleAnywhere, BlueprintReadOnly, Category = Components)
    UStaticMeshComponent* MeshComponent;

    UPROPERTY(EditAnywhere, BlueprintReadWrite, Category = "Materials")
    UMaterialInterface* BaseMaterial;

    UPROPERTY(BlueprintReadOnly, Category = "Materials")
    UMaterialInstanceDynamic* DynamicMaterial;

    UPROPERTY(EditAnywhere, BlueprintReadWrite, Category = "Animation")
    bool bAnimateParameters = true;

    UPROPERTY(EditAnywhere, BlueprintReadWrite, Category = "Animation")
    float AnimationSpeed = 1.0f;

public:
    virtual void BeginPlay() override;
    virtual void Tick(float DeltaTime) override;

    UFUNCTION(BlueprintCallable, Category = "Materials")
    void SetMaterialParameter(FName ParameterName, float Value);

    UFUNCTION(BlueprintCallable, Category = "Materials")
    void SetMaterialVectorParameter(FName ParameterName, FLinearColor Value);

    UFUNCTION(BlueprintCallable, Category = "Materials")
    void SetMaterialTextureParameter(FName ParameterName, UTexture* Texture);

private:
    float TimeAccumulator = 0.0f;
    void UpdateAnimatedParameters();
};

// DynamicMaterialController.cpp
void ADynamicMaterialController::BeginPlay()
{
    Super::BeginPlay();

    if (BaseMaterial && MeshComponent)
    {
        DynamicMaterial = UMaterialInstanceDynamic::Create(BaseMaterial, this);
        MeshComponent->SetMaterial(0, DynamicMaterial);
    }
}

void ADynamicMaterialController::Tick(float DeltaTime)
{
    Super::Tick(DeltaTime);

    if (bAnimateParameters)
    {
        TimeAccumulator += DeltaTime * AnimationSpeed;
        UpdateAnimatedParameters();
    }
}

void ADynamicMaterialController::UpdateAnimatedParameters()
{
    if (DynamicMaterial)
    {
        // Animate various material parameters
        float SineWave = FMath::Sin(TimeAccumulator);
        float CosWave = FMath::Cos(TimeAccumulator);

        // Example parameter animations
        DynamicMaterial->SetScalarParameterValue("TimeValue", TimeAccumulator);
        DynamicMaterial->SetScalarParameterValue("SineWave", SineWave);
        DynamicMaterial->SetVectorParameterValue("ColorShift", 
            FLinearColor(SineWave * 0.5f + 0.5f, CosWave * 0.5f + 0.5f, 1.0f, 1.0f));
    }
}
\`\`\`

## Lighting Systems

### Dynamic Lighting Controller
\`\`\`cpp
// LightingController.h
UCLASS()
class MYGAME_API ALightingController : public AActor
{
    GENERATED_BODY()

public:
    ALightingController();

protected:
    UPROPERTY(VisibleAnywhere, BlueprintReadOnly, Category = Components)
    class UDirectionalLightComponent* SunLight;

    UPROPERTY(VisibleAnywhere, BlueprintReadOnly, Category = Components)
    class USkyLightComponent* SkyLight;

    UPROPERTY(EditAnywhere, BlueprintReadWrite, Category = "Time of Day")
    float TimeOfDay = 12.0f; // 0-24 hours

    UPROPERTY(EditAnywhere, BlueprintReadWrite, Category = "Time of Day")
    float DayDuration = 300.0f; // seconds for full day cycle

    UPROPERTY(EditAnywhere, BlueprintReadWrite, Category = "Time of Day")
    bool bAutoTimeProgression = true;

    UPROPERTY(EditAnywhere, BlueprintReadWrite, Category = "Lighting")
    UCurveFloat* SunIntensityCurve;

    UPROPERTY(EditAnywhere, BlueprintReadWrite, Category = "Lighting")
    UCurveLinearColor* SunColorCurve;

    UPROPERTY(EditAnywhere, BlueprintReadWrite, Category = "Lighting")
    UCurveFloat* SkyLightIntensityCurve;

public:
    virtual void BeginPlay() override;
    virtual void Tick(float DeltaTime) override;

    UFUNCTION(BlueprintCallable, Category = "Time of Day")
    void SetTimeOfDay(float NewTimeOfDay);

    UFUNCTION(BlueprintCallable, Category = "Time of Day")
    void SetAutoTimeProgression(bool bEnabled);

    UFUNCTION(BlueprintImplementableEvent, Category = "Events")
    void OnTimeOfDayChanged(float NewTime);

private:
    void UpdateLighting();
    float GetNormalizedTime() const;
};

// LightingController.cpp
void ALightingController::BeginPlay()
{
    Super::BeginPlay();
    UpdateLighting();
}

void ALightingController::Tick(float DeltaTime)
{
    Super::Tick(DeltaTime);

    if (bAutoTimeProgression)
    {
        TimeOfDay += (DeltaTime / DayDuration) * 24.0f;
        if (TimeOfDay >= 24.0f)
        {
            TimeOfDay -= 24.0f;
        }
        
        UpdateLighting();
        OnTimeOfDayChanged(TimeOfDay);
    }
}

void ALightingController::UpdateLighting()
{
    float NormalizedTime = GetNormalizedTime();
    
    if (SunLight)
    {
        // Calculate sun rotation (0 = dawn, 0.5 = noon, 1 = dusk)
        float SunAngle = (NormalizedTime - 0.25f) * 180.0f; // -45 to 135 degrees
        SunLight->SetWorldRotation(FRotator(-SunAngle, 0, 0));

        // Update sun intensity and color from curves
        if (SunIntensityCurve)
        {
            float Intensity = SunIntensityCurve->GetFloatValue(NormalizedTime);
            SunLight->SetIntensity(Intensity);
        }

        if (SunColorCurve)
        {
            FLinearColor Color = SunColorCurve->GetLinearColorValue(NormalizedTime);
            SunLight->SetLightColor(Color);
        }
    }

    if (SkyLight && SkyLightIntensityCurve)
    {
        float SkyIntensity = SkyLightIntensityCurve->GetFloatValue(NormalizedTime);
        SkyLight->SetIntensity(SkyIntensity);
        SkyLight->RecaptureSky();
    }
}

float ALightingController::GetNormalizedTime() const
{
    return TimeOfDay / 24.0f;
}
\`\`\`

## Environment Interaction System

### Interactive Objects
\`\`\`cpp
// InteractableObject.h
UINTERFACE(BlueprintType)
class UInteractable : public UInterface
{
    GENERATED_BODY()
};

class IInteractable
{
    GENERATED_BODY()

public:
    UFUNCTION(BlueprintNativeEvent, BlueprintCallable, Category = "Interaction")
    void Interact(AActor* InteractingActor);
    virtual void Interact_Implementation(AActor* InteractingActor) = 0;

    UFUNCTION(BlueprintNativeEvent, BlueprintCallable, Category = "Interaction")
    FString GetInteractionText();
    virtual FString GetInteractionText_Implementation() = 0;

    UFUNCTION(BlueprintNativeEvent, BlueprintCallable, Category = "Interaction")
    bool CanInteract(AActor* InteractingActor);
    virtual bool CanInteract_Implementation(AActor* InteractingActor) = 0;
};

UCLASS()
class MYGAME_API AInteractableObject : public AActor, public IInteractable
{
    GENERATED_BODY()

public:
    AInteractableObject();

protected:
    UPROPERTY(VisibleAnywhere, BlueprintReadOnly, Category = Components)
    UStaticMeshComponent* MeshComponent;

    UPROPERTY(VisibleAnywhere, BlueprintReadOnly, Category = Components)
    class USphereComponent* InteractionTrigger;

    UPROPERTY(EditAnywhere, BlueprintReadWrite, Category = "Interaction")
    FString InteractionText = "Press E to interact";

    UPROPERTY(EditAnywhere, BlueprintReadWrite, Category = "Interaction")
    bool bIsInteractable = true;

    UPROPERTY(EditAnywhere, BlueprintReadWrite, Category = "Interaction")
    float InteractionRange = 200.0f;

public:
    // IInteractable interface
    virtual void Interact_Implementation(AActor* InteractingActor) override;
    virtual FString GetInteractionText_Implementation() override;
    virtual bool CanInteract_Implementation(AActor* InteractingActor) override;

    UFUNCTION(BlueprintImplementableEvent, Category = "Interaction")
    void OnInteracted(AActor* InteractingActor);

protected:
    UFUNCTION()
    void OnInteractionTriggerBeginOverlap(UPrimitiveComponent* OverlappedComponent, 
                                         AActor* OtherActor, UPrimitiveComponent* OtherComp, 
                                         int32 OtherBodyIndex, bool bFromSweep, 
                                         const FHitResult& SweepResult);

    UFUNCTION()
    void OnInteractionTriggerEndOverlap(UPrimitiveComponent* OverlappedComponent, 
                                       AActor* OtherActor, UPrimitiveComponent* OtherComp, 
                                       int32 OtherBodyIndex);
};
\`\`\`

## World Streaming and LOD

### Level Streaming
\`\`\`cpp
// LevelStreamingManager.h
UCLASS()
class MYGAME_API ALevelStreamingManager : public AActor
{
    GENERATED_BODY()

public:
    ALevelStreamingManager();

protected:
    UPROPERTY(EditAnywhere, BlueprintReadWrite, Category = "Streaming")
    TArray<FString> StreamingLevels;

    UPROPERTY(EditAnywhere, BlueprintReadWrite, Category = "Streaming")
    float StreamingDistance = 5000.0f;

    UPROPERTY(EditAnywhere, BlueprintReadWrite, Category = "Streaming")
    float UnloadDistance = 7000.0f;

public:
    virtual void BeginPlay() override;
    virtual void Tick(float DeltaTime) override;

    UFUNCTION(BlueprintCallable, Category = "Streaming")
    void LoadLevel(const FString& LevelName);

    UFUNCTION(BlueprintCallable, Category = "Streaming")
    void UnloadLevel(const FString& LevelName);

private:
    TMap<FString, bool> LevelLoadStates;
    AActor* PlayerActor;

    void UpdateLevelStreaming();
    float GetDistanceToLevel(const FString& LevelName);
};
\`\`\`
      `,
      learningObjectives: [
        'Design compelling game environments',
        'Create dynamic material systems',
        'Implement advanced lighting setups',
        'Build interactive world objects'
      ]
    },
    {
      id: 'unreal-deployment',
      title: 'Packaging and Deployment',
      description: 'Deploy Unreal games to multiple platforms with optimization.',
      duration: '2 hours',
      difficulty: 'intermediate',
      content: `
# Packaging and Deployment

## Build Configuration

### Project Settings Optimization
\`\`\`cpp
// ProjectSettings.h - Example configuration class
UCLASS(config=Game, defaultconfig)
class MYGAME_API UMyProjectSettings : public UObject
{
    GENERATED_BODY()

public:
    UPROPERTY(config, EditAnywhere, Category = "Performance")
    bool bEnableOptimizations = true;

    UPROPERTY(config, EditAnywhere, Category = "Performance")
    int32 MaxDrawCalls = 2000;

    UPROPERTY(config, EditAnywhere, Category = "Performance")
    float LODDistanceScale = 1.0f;

    UPROPERTY(config, EditAnywhere, Category = "Graphics")
    int32 DefaultTextureQuality = 2; // 0=Low, 1=Medium, 2=High, 3=Epic

    UPROPERTY(config, EditAnywhere, Category = "Graphics")
    bool bEnableRayTracing = false;

    UPROPERTY(config, EditAnywhere, Category = "Platform")
    FString BuildVersion = "1.0.0";
};
\`\`\`

### Platform-Specific Configurations
\`\`\`cpp
// PlatformManager.h
UCLASS()
class MYGAME_API UPlatformManager : public UGameInstanceSubsystem
{
    GENERATED_BODY()

public:
    virtual void Initialize(FSubsystemCollectionBase& Collection) override;

    UFUNCTION(BlueprintCallable, Category = "Platform")
    void OptimizeForCurrentPlatform();

    UFUNCTION(BlueprintCallable, Category = "Platform")
    FString GetPlatformName();

    UFUNCTION(BlueprintCallable, Category = "Platform")
    bool IsMobilePlatform();

    UFUNCTION(BlueprintCallable, Category = "Platform")
    bool IsConsolePlatform();

    UFUNCTION(BlueprintCallable, Category = "Platform")
    bool IsDesktopPlatform();

private:
    void ApplyMobileOptimizations();
    void ApplyConsoleOptimizations();
    void ApplyDesktopOptimizations();
};

// PlatformManager.cpp
void UPlatformManager::Initialize(FSubsystemCollectionBase& Collection)
{
    Super::Initialize(Collection);
    OptimizeForCurrentPlatform();
}

void UPlatformManager::OptimizeForCurrentPlatform()
{
    #if PLATFORM_ANDROID || PLATFORM_IOS
        ApplyMobileOptimizations();
    #elif PLATFORM_PS4 || PLATFORM_PS5 || PLATFORM_XBOXONE || PLATFORM_XBOXSERIES
        ApplyConsoleOptimizations();
    #elif PLATFORM_WINDOWS || PLATFORM_MAC || PLATFORM_LINUX
        ApplyDesktopOptimizations();
    #endif
}

void UPlatformManager::ApplyMobileOptimizations()
{
    // Mobile-specific settings
    UE_LOG(LogTemp, Warning, TEXT("Applying mobile optimizations"));
    
    // Reduce quality settings
    auto* Settings = GEngine->GetGameRenderSettings();
    if (Settings)
    {
        // Lower shadow quality
        Settings->MaxShadowResolution = 512;
        Settings->ShadowQuality = 1;
        
        // Disable expensive effects
        Settings->bEnableRayTracing = false;
        Settings->ReflectionQuality = 1;
    }
}

void UPlatformManager::ApplyConsoleOptimizations()
{
    UE_LOG(LogTemp, Warning, TEXT("Applying console optimizations"));
    
    // Console-specific settings
    auto* Settings = GEngine->GetGameRenderSettings();
    if (Settings)
    {
        Settings->MaxShadowResolution = 2048;
        Settings->ShadowQuality = 3;
        Settings->ReflectionQuality = 3;
    }
}
\`\`\`

## Asset Optimization

### Texture Streaming
\`\`\`cpp
// TextureStreamingManager.h
UCLASS()
class MYGAME_API UTextureStreamingManager : public UGameInstanceSubsystem
{
    GENERATED_BODY()

public:
    virtual void Initialize(FSubsystemCollectionBase& Collection) override;

    UFUNCTION(BlueprintCallable, Category = "Streaming")
    void SetTextureStreamingPoolSize(int32 PoolSizeMB);

    UFUNCTION(BlueprintCallable, Category = "Streaming")
    void ForceStreamTextures(const TArray<UTexture*>& Textures);

    UFUNCTION(BlueprintCallable, Category = "Streaming")
    void PreloadTexturesForLevel(const FString& LevelName);

    UFUNCTION(BlueprintCallable, Category = "Optimization")
    void OptimizeTexturesForPlatform();

private:
    TMap<FString, TArray<UTexture*>> LevelTextureMap;
    
    void BuildTextureDatabase();
    void CompressTexturesForMobile();
    void SetMipmapSettings();
};

// Asset cooking optimization
UCLASS()
class MYGAME_API UAssetCookingSettings : public UObject
{
    GENERATED_BODY()

public:
    UFUNCTION(CallInEditor = true, Category = "Asset Optimization")
    void OptimizeAllTextures();

    UFUNCTION(CallInEditor = true, Category = "Asset Optimization")
    void OptimizeAllMeshes();

    UFUNCTION(CallInEditor = true, Category = "Asset Optimization")
    void OptimizeAllAudio();

private:
    void OptimizeTexture(UTexture2D* Texture);
    void OptimizeMesh(UStaticMesh* Mesh);
    void OptimizeAudioClip(USoundWave* Audio);
};
\`\`\`

## Packaging Pipeline

### Automated Build System
\`\`\`cpp
// BuildAutomation.h
UCLASS()
class MYGAME_API UBuildAutomation : public UBlueprintFunctionLibrary
{
    GENERATED_BODY()

public:
    UFUNCTION(BlueprintCallable, Category = "Build Automation", CallInEditor = true)
    static void PackageForAllPlatforms();

    UFUNCTION(BlueprintCallable, Category = "Build Automation", CallInEditor = true)
    static void PackageForPlatform(const FString& PlatformName);

    UFUNCTION(BlueprintCallable, Category = "Build Automation", CallInEditor = true)
    static void ValidateAssets();

    UFUNCTION(BlueprintCallable, Category = "Build Automation", CallInEditor = true)
    static void GenerateBuildReport();

private:
    static bool ValidateAllAssets();
    static void CopyAdditionalFiles(const FString& PlatformName);
    static void CreateInstaller(const FString& PlatformName);
};

// Build configuration templates
USTRUCT(BlueprintType)
struct FBuildConfiguration
{
    GENERATED_BODY()

    UPROPERTY(EditAnywhere, BlueprintReadWrite)
    FString PlatformName;

    UPROPERTY(EditAnywhere, BlueprintReadWrite)
    FString Configuration; // Development, Shipping, Debug

    UPROPERTY(EditAnywhere, BlueprintReadWrite)
    FString OutputDirectory;

    UPROPERTY(EditAnywhere, BlueprintReadWrite)
    bool bCreatePakFile = true;

    UPROPERTY(EditAnywhere, BlueprintReadWrite)
    bool bCompressAssets = true;

    UPROPERTY(EditAnywhere, BlueprintReadWrite)
    TArray<FString> AdditionalCookParams;

    UPROPERTY(EditAnywhere, BlueprintReadWrite)
    TArray<FString> MapsToInclude;
};
\`\`\`

## Performance Monitoring

### Runtime Performance Tracking
\`\`\`cpp
// PerformanceTracker.h
UCLASS()
class MYGAME_API APerformanceTracker : public AActor
{
    GENERATED_BODY()

public:
    APerformanceTracker();

protected:
    virtual void BeginPlay() override;
    virtual void Tick(float DeltaTime) override;

    UPROPERTY(EditAnywhere, BlueprintReadWrite, Category = "Monitoring")
    bool bEnableTracking = true;

    UPROPERTY(EditAnywhere, BlueprintReadWrite, Category = "Monitoring")
    float SampleInterval = 1.0f;

    UPROPERTY(BlueprintReadOnly, Category = "Performance Metrics")
    float AverageFPS;

    UPROPERTY(BlueprintReadOnly, Category = "Performance Metrics")
    float AverageFrameTime;

    UPROPERTY(BlueprintReadOnly, Category = "Performance Metrics")
    int32 DrawCalls;

    UPROPERTY(BlueprintReadOnly, Category = "Performance Metrics")
    float MemoryUsageMB;

public:
    UFUNCTION(BlueprintCallable, Category = "Performance")
    void StartProfiling();

    UFUNCTION(BlueprintCallable, Category = "Performance")
    void StopProfiling();

    UFUNCTION(BlueprintCallable, Category = "Performance")
    void ExportPerformanceReport();

    UFUNCTION(BlueprintImplementableEvent, Category = "Events")
    void OnPerformanceWarning(const FString& Warning);

private:
    TArray<float> FrameTimeHistory;
    FTimerHandle SampleTimerHandle;
    bool bIsProfiling = false;

    void SamplePerformanceMetrics();
    void CheckPerformanceThresholds();
    void LogPerformanceData();
};

// Automated testing for builds
UCLASS()
class MYGAME_API UBuildValidator : public UObject
{
    GENERATED_BODY()

public:
    UFUNCTION(BlueprintCallable, Category = "Validation")
    static bool ValidateBuildQuality();

    UFUNCTION(BlueprintCallable, Category = "Validation")
    static TArray<FString> GetValidationErrors();

    UFUNCTION(BlueprintCallable, Category = "Validation")
    static void RunPerformanceTests();

private:
    static bool TestMemoryUsage();
    static bool TestLoadTimes();
    static bool TestFrameRate();
    static bool ValidateAssetReferences();
};
\`\`\`

## Distribution and Updates

### Version Management
\`\`\`cpp
// VersionManager.h
UCLASS()
class MYGAME_API UVersionManager : public UGameInstanceSubsystem
{
    GENERATED_BODY()

public:
    virtual void Initialize(FSubsystemCollectionBase& Collection) override;

    UFUNCTION(BlueprintCallable, Category = "Version")
    FString GetCurrentVersion();

    UFUNCTION(BlueprintCallable, Category = "Version")
    bool CheckForUpdates();

    UFUNCTION(BlueprintCallable, Category = "Version")
    void DownloadUpdate();

    UFUNCTION(BlueprintImplementableEvent, Category = "Events")
    void OnUpdateAvailable(const FString& NewVersion);

    UFUNCTION(BlueprintImplementableEvent, Category = "Events")
    void OnUpdateDownloaded();

private:
    FString CurrentVersion;
    FString UpdateURL;
    
    void ParseVersionFile();
    void CompareVersions();
};
\`\`\`
      `,
      learningObjectives: [
        'Configure builds for multiple platforms',
        'Optimize assets for deployment',
        'Set up automated build pipelines',
        'Monitor performance in shipped builds'
      ]
    }
  ]
};